/**
 * Seed Consolidator
 *
 * Authoritative data loader for the learning system.
 * Imports 10 module-based seed files and validates against Master Taxonomy.
 *
 * Part of the Content domain (DDD Fase 3).
 * Moved from src/data/seedConsolidator.ts in R26.
 * 10 modules: GET, REK, MET, TIJD, GELD, SWW, SPR, TAA, STU, TOPO.
 *
 * Features:
 * - Validates all questions against taxonomy
 * - Enforces tier consistency (tier must match taxonomy)
 * - Auto-corrects skill/subSkill mismatches with warnings
 * - Produces deterministic SeedCollection maps
 */

import type { ModuleId, Question, QuestionCore, QuestionType } from '../types';
import { MODULE_ID_TO_CODE, MODULE_CODE_TO_ID } from '../modules';
import { type VisualAid } from '../../visuals';
import { isValidFamilyId, getFamilyDefinition, getAuthoritativeTier } from '../taxonomy';
import { debug, warn, error as logError, group as logGroup, groupEnd as logGroupEnd } from '../../utils/logger';
// NOTE: governance/pipeline/visualRegistrySync was previously imported here but creates
// a circular dependency (seedConsolidator → visualRegistrySync → content → questionLoader → seedConsolidator).
// The visual sync check is non-critical (diagnostic only) and defaults to inSync: true.
// Full sync checking is available via governance/pipeline/visualRegistrySync directly.

// ============================================================
// Seed Loading (Async — Bundle Optimization Phase 2)
// ============================================================

import { loadAllSeeds, loadModuleSeeds, _populateLegacyExports, type SeedModule } from '../seeds';

// Auto-fill utilities (governance-driven systemic defaults)
import { estimateDok } from '../../governance/utils/dokAutoTagger';
import { MISCONCEPTION_REGISTRY, isKnownMisconception, getMisconception } from '../../governance/data/misconceptions';
import type { MisconceptionTag, PedagogyContract } from '../types';

// ============================================================
// Configuration
// ============================================================

/**
 * STRICT mode: When enabled, validation errors will throw instead of auto-correcting.
 * ENABLED BY DEFAULT to enforce data quality (2026-02-18).
 *
 * Enforces:
 * - Skill values must match taxonomy definitions
 * - SubSkill values must match taxonomy definitions
 *
 * Note: Tier mismatches are always auto-corrected from taxonomy (tier is authoritative).
 */
const STRICT_MODE = true;

// ============================================================
// Types
// ============================================================

export interface SeedQuestionSourceMetadata {
  __sourceFile?: string;
  __sourceSeedKey?: string;
}
export interface SeedQuestion {
  // Identity
  id: string;
  module: ModuleId;
  familyId: string; // REQUIRED — canonical 4.1 familyId

  // Tier is authoritative
  tier: number;
  levelCode: string;
  group: number;

  // Content
  question: string;
  questionType: string;
  context?: string;
  options: string[];
  correctAnswer: number;

  // Skills — REQUIRED (enforced from taxonomy)
  skill: string; // REQUIRED
  subSkill: string; // REQUIRED

  // Taxonomy fields
  conceptId?: string;
  variantIndex?: number;

  /**
   * FG2 Phase 2 — Equivalence class label.
   * Two seeds sharing the same `equivalenceClass` are treated as one for
   * breadth/spread accounting in `projectMasteryToUnlock`. Omit/undefined =
   * legacy one-class-per-question behaviour (every question is its own class).
   * See §S10 of ARCHITECTURAL_PEER_REVIEW_PLAN_2026.
   */
  equivalenceClass?: string;

  // CITO
  citoDifficulty?: number;
  citoDiscrimination?: number;
  isAnchor?: boolean;

  // Universal data payload
  data?: Record<string, unknown>;

  // Canonical feedback
  feedback?: Question['feedback'];

  // Visual Aids (canonical system — STAGE 3)
  stimulusAids?: VisualAid[];
  supportAids?: VisualAid[];

  // Pedagogical contract (PEDAGOGICAL_CONTRACT_SPEC §3)
  pedagogy?: QuestionCore['pedagogy'];

  // Dev diagnostics provenance
  __sourceFile?: string;
  __sourceSeedKey?: string;
}
export interface SeedCollection {
  module: ModuleId;
  moduleCode: string;
  version: string;
  taxonomyVersion: string;
  questions: SeedQuestion[];
}

// ============================================================
// Module Code Mapping — imported from canonical source (modules.ts)
// ============================================================

/** @deprecated Use MODULE_ID_TO_CODE from content/modules.ts directly */
export const MODULE_CODES = MODULE_ID_TO_CODE;

// ============================================================
// Tier → Group Mapping
// ============================================================

function getGroupFromTier(tier: number): number {
  if (tier <= 6) return 6;
  if (tier <= 12) return 7;
  if (tier <= 16) return 8;
  return 8;
}
const DEPRECATED_SEED_KEYS = ['visualAid', 'explanation', 'explanationPerOption', 'explanationCorrect', 'distractorExplanations'];
const DEPRECATED_FEEDBACK_KEYS = ['feedback.explanation', 'feedback.explanationPerOption', 'feedback.stimulusAids', 'feedback.supportAids'];
function assertCanonicalSeedPayload(raw: Record<string, unknown>): void {
  const deprecated: string[] = [];
  const feedback = (raw.feedback ?? {}) as Record<string, unknown>;
  DEPRECATED_SEED_KEYS.forEach(key => {
    if (key in raw) deprecated.push(key);
  });
  DEPRECATED_FEEDBACK_KEYS.forEach(key => {
    const nested = key.split('.')[1];
    if (nested && nested in feedback) deprecated.push(key);
  });
  if (deprecated.length > 0) {
    throw new Error(`Question ${String(raw.id ?? 'unknown')} contains deprecated keys: ${deprecated.join(', ')}.`);
  }
}

// ============================================================
// Pedagogy Auto-Fill (DOK + MisconceptionTags)
// ============================================================

/**
 * Inject a heuristic DOK level when the seed has none.
 *
 * Only fills when estimateDok confidence is `high` or `medium` — low-confidence
 * estimates are skipped so they remain visible to governance as SEED_DOK_MISSING.
 * Auto-filled values are marked with `dokAutoFilled: true` so the admin UI can
 * surface them for human confirmation.
 */
function autoFillDok(rawForEstimator: Question, pedagogy: PedagogyContract | undefined): PedagogyContract | undefined {
  if (pedagogy?.dok !== undefined) return pedagogy;
  const estimate = estimateDok(rawForEstimator);
  if (estimate.confidence === 'low') return pedagogy;
  return {
    ...(pedagogy ?? {}),
    dok: estimate.dok,
    dokAutoFilled: true
  };
}

/**
 * Generate stub misconceptionTags for MC distractors when the seed has none.
 *
 * Looks up MISCONCEPTION_REGISTRY entries whose `typicalFamilies` includes the
 * question's familyId, then cycles them across the distractor option indices.
 *
 * This is a best-effort stub — some seeds will get imperfect or mismatched
 * tags because not every distractor maps cleanly to a registered misconception.
 * The point is to make the system signal-bearing by default; authors override
 * the stubs with accurate tags as families are reviewed.
 *
 * Auto-filled tags are marked with `misconceptionsAutoFilled: true`.
 */
function autoFillMisconceptionTags(questionType: string, familyId: string, options: string[], correctAnswer: number, pedagogy: PedagogyContract | undefined): PedagogyContract | undefined {
  // Only MC questions have distractor tagging
  if (questionType !== 'multiple_choice') return pedagogy;
  // Skip if already tagged
  if (pedagogy?.misconceptionTags && pedagogy.misconceptionTags.length > 0) {
    return pedagogy;
  }
  // Need at least 2 options (one correct + ≥1 distractor)
  if (!Array.isArray(options) || options.length < 2) return pedagogy;

  // Find registered misconceptions that mention this family
  const candidates = MISCONCEPTION_REGISTRY.filter(m => m.typicalFamilies?.includes(familyId));
  if (candidates.length === 0) return pedagogy;

  // Build a tag for each distractor by cycling through candidates
  const tags: MisconceptionTag[] = [];
  let cursor = 0;
  for (let i = 0; i < options.length; i++) {
    if (i === correctAnswer) continue;
    const m = candidates[cursor % candidates.length];
    if (!m) break;
    tags.push({
      id: m.id,
      label: m.label,
      optionIndex: i
    });
    cursor++;
  }
  if (tags.length === 0) return pedagogy;
  return {
    ...(pedagogy ?? {}),
    misconceptionTags: tags,
    misconceptionsAutoFilled: true
  };
}

// ============================================================
// Normalize Raw Seed → SeedQuestion
// ============================================================

function normalizeSeedQuestion(raw: Record<string, unknown>, moduleId: ModuleId, capturedWarnings?: string[]): SeedQuestion | null {
  // Validate familyId
  const rawFamilyId = raw.familyId;
  if (!rawFamilyId) {
    logError(`SEED ERROR: Question ${raw.id} is missing familyId`);
    return null;
  }
  if (!isValidFamilyId(rawFamilyId)) {
    logError(`SEED ERROR: Question ${raw.id} has invalid familyId "${rawFamilyId}"`);
    return null;
  }

  // Look up the taxonomy-authoritative tier for this familyId
  const taxonomyTier = getAuthoritativeTier(rawFamilyId);
  const familyDef = getFamilyDefinition(rawFamilyId);

  // Validate and enforce tier from taxonomy
  let actualTier: number;
  const rawTier = raw.tier;
  if (typeof rawTier !== 'number' || rawTier < 1) {
    if (taxonomyTier !== undefined) {
      const msg = `SEED TIER-LOCK: Question ${raw.id} has invalid tier (${rawTier}), auto-corrected to taxonomy tier ${taxonomyTier} for family ${rawFamilyId}`;
      warn(msg);
      capturedWarnings?.push(msg);
      actualTier = taxonomyTier;
    } else {
      logError(`SEED ERROR: Question ${raw.id} has invalid tier (${rawTier}) and no taxonomy fallback`);
      return null;
    }
  } else if (rawTier > 16) {
    if (taxonomyTier !== undefined) {
      const msg = `SEED TIER-LOCK: Question ${raw.id} has out-of-range tier ${rawTier}, auto-corrected to taxonomy tier ${taxonomyTier} for family ${rawFamilyId}`;
      warn(msg);
      capturedWarnings?.push(msg);
      actualTier = taxonomyTier;
    } else {
      logError(`SEED ERROR: Question ${raw.id} has out-of-range tier ${rawTier} (valid: 1-16)`);
      return null;
    }
  } else if (taxonomyTier !== undefined && rawTier !== taxonomyTier) {
    const gap = Math.abs(rawTier - taxonomyTier);
    const msg = `SEED TIER-LOCK: Question ${raw.id} (family ${rawFamilyId}) has tier ${rawTier} but taxonomy defines tier ${taxonomyTier}. ` + `Auto-corrected to ${taxonomyTier}. [Expected: ${familyDef?.levelCode || '?'}]`;

    // De taxonomie is leidend voor de tier, en dat blijft zo — de familie
    // bepaalt het niveau, niet het seedbestand. Maar alleen het LABEL wordt
    // bijgesteld; de vraag zelf wordt niet gecontroleerd. Bij een gat van twee
    // tiers of meer is de vraag voor een ander niveau geschreven, en die komt
    // dan in de selectiewindow van een kind terecht waarvoor hij nooit bedoeld
    // was. Dat is geen administratieve afwijking meer.
    if (gap >= 2) {
      logError(`${msg} LET OP: verschil van ${gap} tiers — deze vraag is voor een ander ` + 'niveau geschreven en wordt nu wél aan kinderen van dat andere niveau ' + 'gesteld. Alleen het label is bijgesteld, de inhoud niet.');
    } else {
      warn(msg);
    }
    capturedWarnings?.push(msg);
    actualTier = taxonomyTier;
  } else {
    actualTier = rawTier;
  }
  const group = getGroupFromTier(actualTier);

  // Derive levelCode from tier (canonical, not from raw data)
  const levelCode = familyDef?.levelCode ?? `T${actualTier}`;

  // Validate skill/subSkill presence
  if (!raw.skill) {
    logError(`SEED ERROR: Question ${raw.id} is missing skill`);
    return null;
  }
  if (!raw.subSkill) {
    logError(`SEED ERROR: Question ${raw.id} is missing subSkill`);
    return null;
  }
  // Canonical payload check — dev only (skip in production for faster startup)
  if (import.meta.env?.DEV) {
    try {
      assertCanonicalSeedPayload(raw as Record<string, unknown>);
    } catch (err) {
      logError(`SEED ERROR: Question ${raw.id} has non-canonical payload:`, err);
      return null;
    }
  }
  if (typeof raw.questionType !== 'string' || !raw.questionType.trim()) {
    logError(`SEED ERROR: Question ${raw.id} is missing required canonical questionType`);
    return null;
  }

  // Enforce skill/subSkill from taxonomy
  let actualSkill = raw.skill;
  let actualSubSkill = raw.subSkill;
  if (familyDef) {
    // De melding hangt af van wat er WERKELIJK gebeurt. Hier stond één tekst
    // met "Auto-corrected." die in beide takken werd gebruikt — terwijl de
    // strikte tak de vraag juist WEGGOOIT. Met `STRICT_MODE = true` (de enige
    // stand die deze build kent) las de console dus "auto-corrected" bij élke
    // mismatch, terwijl die vraag in werkelijkheid uit de content verdween.
    //
    // Dat is de duurste soort logregel: hij beschrijft het tegenovergestelde
    // van wat er gebeurt, dus niemand gaat op zoek naar ontbrekende vragen.
    if (raw.skill !== familyDef.skill) {
      const mismatch = `SEED SKILL-LOCK: Question ${raw.id} (family ${rawFamilyId}) has skill ` + `"${raw.skill}" but taxonomy defines "${familyDef.skill}".`;
      if (STRICT_MODE) {
        logError(`${mismatch} GEWEIGERD — deze vraag komt NIET in de content-pool ` + 'en wordt dus aan geen enkel kind gesteld. Corrigeer het seedbestand.');
        return null;
      } else {
        warn(`${mismatch} Auto-corrected naar de taxonomie.`);
        capturedWarnings?.push(mismatch);
        actualSkill = familyDef.skill;
      }
    }
    if (raw.subSkill !== familyDef.subSkill) {
      const mismatch = `SEED SUBSKILL-LOCK: Question ${raw.id} (family ${rawFamilyId}) has subSkill ` + `"${raw.subSkill}" but taxonomy defines "${familyDef.subSkill}".`;
      if (STRICT_MODE) {
        logError(`${mismatch} GEWEIGERD — deze vraag komt NIET in de content-pool ` + 'en wordt dus aan geen enkel kind gesteld. Corrigeer het seedbestand.');
        return null;
      } else {
        warn(`${mismatch} Auto-corrected naar de taxonomie.`);
        capturedWarnings?.push(mismatch);
        actualSubSkill = familyDef.subSkill;
      }
    }
  }

  // ── Pedagogy auto-fill (systemic governance defaults) ─────────────────────
  // Step 1: DOK heuristic injection (when confidence >= medium)
  let pedagogy = raw.pedagogy as PedagogyContract | undefined;
  const estimatorInput = {
    question: raw.question,
    context: raw.context,
    tier: actualTier,
    // S1 (GET_SSOT_DOK_GOVERNANCE_AUDIT_2026-06 §3.4): pass module so the
    // estimator can apply its module-cognitive-profile DOK floor/ceiling.
    // Without this the auto-fill path was profile-blind and left recall
    // families lingering as low-confidence SEED_DOK_MISSING noise.
    module: moduleId,
    questionType: raw.questionType,
    pedagogy
  } as unknown as Question;
  pedagogy = autoFillDok(estimatorInput, pedagogy);
  // Step 2: MisconceptionTags stub generation for untagged MC distractors
  pedagogy = autoFillMisconceptionTags(raw.questionType, rawFamilyId, raw.options as string[] ?? [], raw.correctAnswer as number ?? 0, pedagogy);

  // Step 3: Legacy format normalization (string-array → object-array).
  //
  // Some seeds ship `pedagogy.misconceptionTags` as a string-array (legacy
  // format) or contain empty/malformed entries. Normalize to the canonical
  // `[{ id, label, optionIndex }]` shape BEFORE the unknown-id check below
  // so we emit a clear FORMAT-LEGACY warning per question instead of the
  // misleading "unknown tag id: <undefined>" noise.
  //
  // Empty/malformed entries are dropped entirely (they are seed bugs).
  // Legacy string-ids are looked up against the registry to populate `label`.
  if (pedagogy?.misconceptionTags && Array.isArray(pedagogy.misconceptionTags) && pedagogy.misconceptionTags.length > 0) {
    const rawTags = pedagogy.misconceptionTags as unknown[];
    const hasLegacyString = rawTags.some(t => typeof t === 'string');
    const hasEmptyEntries = rawTags.some(t => {
      if (typeof t === 'string') return t.trim() === '';
      if (t && typeof t === 'object') {
        const id = (t as {
          id?: unknown;
        }).id;
        return typeof id !== 'string' || id.trim() === '';
      }
      return true;
    });
    if (hasLegacyString || hasEmptyEntries) {
      const normalized: MisconceptionTag[] = [];
      const droppedEmpty: number[] = [];
      const legacyStringIds: string[] = [];
      rawTags.forEach((t, idx) => {
        if (typeof t === 'string') {
          const id = t.trim();
          if (!id) {
            droppedEmpty.push(idx);
            return;
          }
          legacyStringIds.push(id);
          const def = getMisconception(id);
          normalized.push({
            id,
            label: def?.label ?? id
            // optionIndex unknown for legacy string format
          });
        } else if (t && typeof t === 'object') {
          const obj = t as {
            id?: unknown;
            label?: unknown;
            optionIndex?: unknown;
          };
          if (typeof obj.id !== 'string' || obj.id.trim() === '') {
            droppedEmpty.push(idx);
            return;
          }
          normalized.push({
            id: obj.id,
            label: typeof obj.label === 'string' && obj.label ? obj.label : getMisconception(obj.id)?.label ?? obj.id,
            optionIndex: typeof obj.optionIndex === 'number' ? obj.optionIndex : undefined
          });
        } else {
          droppedEmpty.push(idx);
        }
      });
      const parts: string[] = [];
      if (legacyStringIds.length > 0) {
        parts.push(`converted ${legacyStringIds.length} legacy string-array tag(s) → object-array: ${legacyStringIds.join(', ')}`);
      }
      if (droppedEmpty.length > 0) {
        parts.push(`dropped ${droppedEmpty.length} empty/malformed tag entry(ies) at index ${droppedEmpty.join(', ')}`);
      }
      const msg = `SEED FORMAT-LEGACY: Question ${raw.id} (family ${rawFamilyId}) misconceptionTags ` + `auto-normalized — ${parts.join('; ')}. ` + `Fix the seed to use canonical [{ id, label, optionIndex }] format.`;
      warn(msg);
      capturedWarnings?.push(msg);
      pedagogy = {
        ...pedagogy,
        misconceptionTags: normalized
      };
    }
  }

  // Step 4: Build-time validation — surface unknown misconception tag IDs
  // immediately at seed load so they show up in the build log instead of
  // only when the governance pipeline runs (sync audit Phase D-1).
  // Auto-filled tags are skipped because they were drawn from the registry.
  // Runs AFTER Step 3 so it sees the canonical object-array shape.
  if (pedagogy?.misconceptionTags && pedagogy.misconceptionTags.length > 0 && !pedagogy.misconceptionsAutoFilled) {
    const unknown = pedagogy.misconceptionTags.filter(t => !isKnownMisconception(t.id));
    if (unknown.length > 0) {
      const msg = `SEED MISCONCEPTION-UNKNOWN: Question ${raw.id} (family ${rawFamilyId}) references ` + `${unknown.length} unknown misconception tag(s): ${unknown.map(t => t.id).join(', ')}. ` + `Register them in src/governance/data/misconceptions/<module>.ts or correct the IDs.`;
      warn(msg);
      capturedWarnings?.push(msg);
    }
  }
  return {
    // Identity
    id: raw.id,
    module: moduleId,
    familyId: rawFamilyId,
    // Tier is authoritative (from taxonomy, not raw)
    tier: actualTier,
    levelCode,
    group,
    // Content
    question: raw.question,
    questionType: raw.questionType,
    context: raw.context,
    options: raw.options ?? [],
    correctAnswer: raw.correctAnswer ?? 0,
    // Skills — ENFORCED from taxonomy
    skill: actualSkill,
    subSkill: actualSubSkill,
    // Taxonomy fields
    conceptId: raw.conceptId,
    variantIndex: raw.variantIndex ?? 1,
    // CITO
    citoDifficulty: raw.citoDifficulty,
    citoDiscrimination: raw.citoDiscrimination,
    isAnchor: raw.isAnchor,
    // Universal data payload
    data: raw.data,
    // Canonical feedback
    feedback: raw.feedback,
    // Visual Aids (canonical system — STAGE 3)
    stimulusAids: raw.stimulusAids,
    supportAids: raw.supportAids,
    // Pedagogical contract — with auto-filled DOK + misconceptionTags
    pedagogy,
    __sourceFile: raw.__sourceFile,
    __sourceSeedKey: raw.__sourceSeedKey
  };
}

// ============================================================
// Build Seed Collections
// ============================================================

function buildSeedCollections(seeds: SeedModule[]): Map<ModuleId, SeedCollection> {
  const collections = new Map<ModuleId, SeedCollection>();
  const seenIds = new Set<string>();
  const errors: string[] = [];
  const warnings: string[] = [];
  const seedFiles = seeds.map(seed => ({
    key: `seed_${seed.moduleCode}`,
    sourceFile: `src/content/seeds/${seed.moduleCode}/index.ts`,
    data: seed
  }));
  let rejectedCount = 0;
  for (const seedSource of seedFiles) {
    const seedFile = seedSource.data;
    const moduleCode = seedFile.moduleCode;
    const moduleId = MODULE_CODE_TO_ID[moduleCode];
    if (!moduleId) {
      errors.push(`Unknown module code: ${moduleCode}`);
      continue;
    }
    const collection: SeedCollection = {
      module: moduleId,
      moduleCode,
      version: seedFile.version,
      taxonomyVersion: seedFile.taxonomyVersion,
      questions: []
    };
    for (const rawQ of seedFile.questions) {
      const withSource = {
        ...rawQ,
        __sourceFile: rawQ.__sourceFile ?? seedSource.sourceFile,
        __sourceSeedKey: seedSource.key
      };
      // Deduplicate
      if (seenIds.has(rawQ.id)) {
        errors.push(`Duplicate question ID: ${rawQ.id}`);
        rejectedCount++;
        continue;
      }
      seenIds.add(rawQ.id);
      const seed = normalizeSeedQuestion(withSource, moduleId, warnings);
      if (!seed) {
        // normalizeSeedQuestion already logged the error
        rejectedCount++;
        continue;
      }
      collection.questions.push(seed);
    }

    // Sort by tier then ID
    collection.questions.sort((a, b) => {
      if (a.tier !== b.tier) return a.tier - b.tier;
      return a.id.localeCompare(b.id);
    });
    collections.set(moduleId, collection);
  }

  // Store build-time metadata for consolidation report
  _buildErrors = errors;
  _buildWarnings = warnings;
  _buildRejectedCount = rejectedCount;

  // Log results
  if (import.meta.env?.MODE === 'development') {
    logGroup('🌱 Seed Consolidator v4.2');
    debug(`Loaded ${seedFiles.length} seed files`);
    debug(`Total questions: ${seenIds.size}`);
    debug(`Rejected questions: ${rejectedCount}`);
    debug(`STRICT mode: ${STRICT_MODE ? 'ENABLED' : 'DISABLED'}`);
    for (const [moduleId, collection] of collections) {
      const families = new Set(collection.questions.map(q => q.familyId));
      debug(`  ${collection.moduleCode} (${moduleId}): ${collection.questions.length} questions, ${families.size} families`);
    }
    if (errors.length > 0) {
      logError(`${errors.length} errors during seed loading:`);
      errors.forEach(e => logError(`  ${e}`));
    }
    logGroupEnd();
  }
  return collections;
}

/**
 * Build a single SeedCollection from one SeedModule (used for per-module
 * incremental loading via ensureModuleSeedsLoaded).
 *
 * Honors the same dedup-by-id contract as buildSeedCollections by checking
 * against already-loaded SEED_COLLECTIONS.
 */
function buildSingleSeedCollection(seedFile: SeedModule): SeedCollection | null {
  const moduleCode = seedFile.moduleCode;
  const moduleId = MODULE_CODE_TO_ID[moduleCode];
  if (!moduleId) {
    _buildErrors.push(`Unknown module code: ${moduleCode}`);
    return null;
  }

  // Build set of IDs already loaded across other modules for cross-seed dedup
  const seenIds = new Set<string>();
  for (const existing of SEED_COLLECTIONS.values()) {
    for (const q of existing.questions) seenIds.add(q.id);
  }
  const sourceFile = `src/content/seeds/${moduleCode}/index.ts`;
  const sourceKey = `seed_${moduleCode}`;
  const collection: SeedCollection = {
    module: moduleId,
    moduleCode,
    version: seedFile.version,
    taxonomyVersion: seedFile.taxonomyVersion,
    questions: []
  };
  for (const rawQ of seedFile.questions) {
    const withSource = {
      ...rawQ,
      __sourceFile: rawQ.__sourceFile ?? sourceFile,
      __sourceSeedKey: sourceKey
    };
    if (seenIds.has(rawQ.id)) {
      _buildErrors.push(`Duplicate question ID: ${rawQ.id}`);
      _buildRejectedCount++;
      continue;
    }
    seenIds.add(rawQ.id);
    const seed = normalizeSeedQuestion(withSource, moduleId, _buildWarnings);
    if (!seed) {
      _buildRejectedCount++;
      continue;
    }
    collection.questions.push(seed);
  }
  collection.questions.sort((a, b) => {
    if (a.tier !== b.tier) return a.tier - b.tier;
    return a.id.localeCompare(b.id);
  });
  return collection;
}

// Build-time metadata (captured during buildSeedCollections, consumed by getConsolidationReport)
let _buildErrors: string[] = [];
let _buildWarnings: string[] = [];
let _buildRejectedCount = 0;

// ============================================================
// Validation (for dev panels)
// ============================================================

import { TAXONOMY } from '../taxonomy';

/**
 * Validate the seed system against Master Taxonomy 4.1.
 * Checks for 100% familyId coverage.
 */
export function validateSeedCoverage(): {
  isValid: boolean;
  totalFamilies: number;
  coveredFamilies: number;
  missingFamilies: string[];
  extraFamilies: string[];
  duplicateFamilies: string[];
  totalQuestions: number;
  questionsByModule: Record<string, number>;
  errors: string[];
  warnings: string[];
} {
  const report = {
    isValid: false,
    totalFamilies: TAXONOMY.length,
    coveredFamilies: 0,
    missingFamilies: [] as string[],
    extraFamilies: [] as string[],
    duplicateFamilies: [] as string[],
    totalQuestions: 0,
    questionsByModule: {} as Record<string, number>,
    errors: [] as string[],
    warnings: [] as string[]
  };
  const taxonomyFamilyIds = new Set(TAXONOMY.map(f => f.familyId));
  const seedFamilyIds = new Set<string>();
  const familyIdCounts = new Map<string, number>();
  for (const collection of SEED_COLLECTIONS.values()) {
    report.questionsByModule[collection.moduleCode] = collection.questions.length;
    report.totalQuestions += collection.questions.length;
    for (const q of collection.questions) {
      seedFamilyIds.add(q.familyId);
      familyIdCounts.set(q.familyId, (familyIdCounts.get(q.familyId) ?? 0) + 1);
    }
  }
  for (const familyId of taxonomyFamilyIds) {
    if (seedFamilyIds.has(familyId)) {
      report.coveredFamilies++;
    } else {
      report.missingFamilies.push(familyId);
    }
  }
  for (const familyId of seedFamilyIds) {
    if (!taxonomyFamilyIds.has(familyId)) {
      report.extraFamilies.push(familyId);
    }
  }
  for (const [familyId, count] of familyIdCounts) {
    if (count > 1) {
      report.duplicateFamilies.push(familyId);
    }
  }
  if (report.missingFamilies.length > 0) {
    report.errors.push(`Missing ${report.missingFamilies.length} familyIds from taxonomy`);
  }
  if (report.extraFamilies.length > 0) {
    report.warnings.push(`${report.extraFamilies.length} familyIds in seeds not in taxonomy`);
  }
  report.isValid = report.missingFamilies.length === 0 && report.extraFamilies.length === 0;
  return report;
}

/**
 * Validate individual seed questions for structural correctness.
 */
export function validateSeedQuestionsLocal(): {
  valid: number;
  invalid: number;
  issues: {
    id: string;
    problems: string[];
  }[];
} {
  const issues: {
    id: string;
    problems: string[];
  }[] = [];
  let valid = 0;
  let invalid = 0;
  for (const collection of SEED_COLLECTIONS.values()) {
    for (const q of collection.questions) {
      const problems: string[] = [];
      if (!q.id) problems.push('Missing id');
      if (!q.module) problems.push('Missing module');
      if (!q.familyId) problems.push('Missing familyId');
      if (!q.question) problems.push('Missing question text');
      if (!q.skill) problems.push('Missing skill');
      if (!q.subSkill) problems.push('Missing subSkill');
      if (q.tier < 1 || q.tier > 16) problems.push(`Invalid tier: ${q.tier}`);
      if (!q.feedback?.explanationText?.trim()) {
        problems.push('Missing canonical feedback.explanationText');
      }
      const qType = q.questionType;
      if (qType === 'multiple_choice') {
        if (!q.options || q.options.length < 2) problems.push('MC needs at least 2 options');
        if (q.correctAnswer === undefined || q.correctAnswer < 0 || q.correctAnswer >= (q.options?.length ?? 0)) problems.push('Invalid correctAnswer index');
        const distractorExplanations = q.feedback?.distractorExplanations;
        if (!distractorExplanations) {
          problems.push('Missing canonical feedback.distractorExplanations for MCQ option coverage');
        } else if (q.options && q.options.length > 0) {
          q.options.forEach((_, optionIndex) => {
            const explanation = distractorExplanations[optionIndex.toString()];
            if (typeof explanation !== 'string' || explanation.trim() === '') {
              problems.push(`Missing canonical feedback.distractorExplanations[${optionIndex}]`);
            }
          });
        }

        // === NEW: Question quality checks (Part 3) ===

        // Check for duplicate options
        if (q.options && q.options.length > 0) {
          const normalizedOptions = q.options.map((o: string) => o.trim().toLowerCase());
          const uniqueOptions = new Set(normalizedOptions);
          if (uniqueOptions.size < normalizedOptions.length) {
            problems.push('Duplicate options detected');
          }
        }

        // Check that no distractor is identical to the correct answer
        if (q.options && q.correctAnswer !== undefined && q.correctAnswer >= 0) {
          const correctText = (q.options[q.correctAnswer] || '').trim().toLowerCase();
          q.options.forEach((opt: string, idx: number) => {
            if (idx !== q.correctAnswer && opt.trim().toLowerCase() === correctText) {
              problems.push(`Distractor at index ${idx} is identical to correct answer`);
            }
          });
        }

        // Check for empty options
        if (q.options) {
          q.options.forEach((opt: string, idx: number) => {
            if (!opt || opt.trim() === '') {
              problems.push(`Empty option at index ${idx}`);
            }
          });
        }
      }

      // Interactive type validation: require data.{type}
      // NOTE: Some types use canonical nested format (data.clock = {...})
      // while others use flat format (data = { acceptedAnswers: [...] }).
      // We accept EITHER: nested key matching type name, or flat data with any keys.
      const hasDataPayload = (type: string) => q.data && typeof q.data === 'object' && (type in q.data || Object.keys(q.data).length > 0);
      if (qType === 'clock' && !hasDataPayload('clock')) problems.push('Clock type needs data.clock');
      if (qType === 'table_reading' && !hasDataPayload('table_reading')) problems.push('Table type needs data.table_reading');
      if (qType === 'bar_chart' && !hasDataPayload('bar_chart')) problems.push('Bar chart type needs data.bar_chart');
      if (qType === 'schedule_reading' && !hasDataPayload('schedule_reading')) problems.push('Schedule type needs data.schedule_reading');
      if (qType === 'fill_in' && !hasDataPayload('fill_in')) problems.push('Fill-in type needs data.fill_in');
      if (qType === 'ratio_table' && !hasDataPayload('ratio_table')) problems.push('Ratio table type needs data.ratio_table');
      if (qType === 'line_chart' && !hasDataPayload('line_chart')) problems.push('Line chart type needs data.line_chart');
      if (qType === 'number_sequence' && !hasDataPayload('number_sequence')) problems.push('Number sequence type needs data.number_sequence');
      if (qType === 'ordering' && !hasDataPayload('ordering')) problems.push('Ordering type needs data.ordering');
      if (qType === 'matching' && !hasDataPayload('matching')) problems.push('Matching type needs data.matching');
      if (qType === 'money' && !hasDataPayload('money')) problems.push('Money type needs data.money');
      if (qType === 'drag_drop_fill' && !hasDataPayload('drag_drop_fill')) problems.push('Drag-drop-fill type needs data.drag_drop_fill');
      if (qType === 'text_highlight' && !hasDataPayload('text_highlight')) problems.push('Text-highlight type needs data.text_highlight');
      if (qType === 'pie_chart' && !hasDataPayload('pie_chart')) problems.push('Pie chart type needs data.pie_chart');
      if ((qType === 'cijferend_rekenen' || qType === 'column_arithmetic') && !hasDataPayload('columnArithmetic') && !hasDataPayload('column_arithmetic')) problems.push('Column arithmetic type needs data.columnArithmetic');

      // Verify exactly one correct answer for all types with options
      if (q.options && q.options.length > 0) {
        if (q.correctAnswer === undefined || q.correctAnswer === null) {
          problems.push('Missing correctAnswer for question with options');
        }
      }

      // citoDifficulty range validation (0.15–0.75)
      if (q.citoDifficulty !== undefined) {
        if (q.citoDifficulty < 0.15 || q.citoDifficulty > 0.75) {
          problems.push(`citoDifficulty ${q.citoDifficulty} out of valid range (0.15–0.75)`);
        }
      }

      // conceptId presence check
      if (!q.conceptId || typeof q.conceptId === 'string' && !q.conceptId.trim()) {
        problems.push('Missing conceptId');
      }
      if (problems.length > 0) {
        invalid++;
        issues.push({
          id: q.id,
          problems
        });
      } else {
        valid++;
      }
    }
  }
  return {
    valid,
    invalid,
    issues
  };
}

/**
 * Get the question type distribution across all seeds.
 */
export function getSeedQuestionTypeDistribution(): Record<string, number> {
  const dist: Record<string, number> = {};
  for (const collection of SEED_COLLECTIONS.values()) {
    for (const q of collection.questions) {
      const type = q.questionType;
      dist[type] = (dist[type] ?? 0) + 1;
    }
  }
  return dist;
}

/**
 * Validate cross-seed duplicate question IDs.
 *
 * RESOLVES: SYSTEM_PLAN_OF_APPROACH item #3 (Cross-seed duplicate question ID check)
 *
 * Checks for question IDs that appear in multiple seed files.
 * The buildSeedCollections() function already deduplicates at load time,
 * but this provides a diagnostic report for the developer panel.
 */
export function validateCrossSeedDuplicateIds(): {
  hasDuplicates: boolean;
  totalQuestions: number;
  uniqueIds: number;
  duplicates: {
    id: string;
    occurrences: {
      module: string;
      sourceFile: string;
    }[];
  }[];
} {
  const idOccurrences = new Map<string, {
    module: string;
    sourceFile: string;
  }[]>();
  for (const collection of SEED_COLLECTIONS.values()) {
    for (const q of collection.questions) {
      if (!idOccurrences.has(q.id)) {
        idOccurrences.set(q.id, []);
      }
      idOccurrences.get(q.id)!.push({
        module: collection.moduleCode,
        sourceFile: q.__sourceFile ?? 'unknown'
      });
    }
  }
  const duplicates: {
    id: string;
    occurrences: {
      module: string;
      sourceFile: string;
    }[];
  }[] = [];
  let totalQuestions = 0;
  for (const [id, occurrences] of idOccurrences) {
    totalQuestions += occurrences.length;
    if (occurrences.length > 1) {
      duplicates.push({
        id,
        occurrences
      });
    }
  }
  return {
    hasDuplicates: duplicates.length > 0,
    totalQuestions,
    uniqueIds: idOccurrences.size,
    duplicates
  };
}

// ============================================================
// Exports
// ============================================================

/** All 10 seed collections — populated by initializeSeedCollections() */
export const SEED_COLLECTIONS: Map<ModuleId, SeedCollection> = new Map();

/** Whether the seed system has been initialized (all modules loaded) */
let _seedSystemReady = false;

/** Per-module loaded set for incremental loading */
const _loadedModuleCodes = new Set<string>();

/** Check if the seed system is ready for synchronous access */
export function isSeedSystemReady(): boolean {
  return _seedSystemReady;
}

/** Check if a specific module's seeds have been loaded */
export function isModuleSeedsLoaded(moduleCode: string): boolean {
  return _loadedModuleCodes.has(moduleCode);
}

/**
 * Initialize the seed system by loading all module seeds asynchronously.
 * Must be called once at app startup before any synchronous seed access.
 * After this resolves, all existing synchronous exports (SEED_COLLECTIONS,
 * getAllSeedQuestions, etc.) work exactly as before.
 *
 * Bundle Optimization Phase 2 — mei 2026.
 */
export async function initializeSeedCollections(): Promise<void> {
  if (_seedSystemReady) return; // idempotent

  const seeds = await loadAllSeeds();
  _populateLegacyExports(seeds);
  const built = buildSeedCollections(seeds);
  for (const [key, value] of built) {
    SEED_COLLECTIONS.set(key, value);
  }
  for (const seed of seeds) {
    _loadedModuleCodes.add(seed.moduleCode);
  }
  _seedSystemReady = true;
}

/**
 * Load and build a single module's seed collection in-place.
 * Idempotent — does nothing if the module is already loaded.
 *
 * Returns the SeedCollection that was added (or already present).
 */
export async function ensureModuleSeedsLoaded(moduleCode: string): Promise<SeedCollection | null> {
  if (_loadedModuleCodes.has(moduleCode)) {
    const moduleId = MODULE_CODE_TO_ID[moduleCode];
    return moduleId ? SEED_COLLECTIONS.get(moduleId) ?? null : null;
  }
  const seedFile = await loadModuleSeeds(moduleCode);
  _populateLegacyExports([seedFile]);
  const collection = buildSingleSeedCollection(seedFile);
  if (!collection) return null;
  SEED_COLLECTIONS.set(collection.module, collection);
  _loadedModuleCodes.add(moduleCode);
  return collection;
}

// ─── §4.2 Consolidation Report ───────────────────────────────────────────────

/**
 * Structured consolidation report — surfaces hidden load errors in admin/dev.
 *
 * RESOLVES: SYSTEM_STABILIZATION_BLUEPRINT §4.2
 */
export interface ConsolidationReport {
  /** Total seed files processed */
  totalSeedFiles: number;
  /** Total questions successfully loaded */
  totalQuestions: number;
  /** Questions rejected during normalisation */
  rejectedQuestions: number;
  /** Per-module breakdown */
  modules: {
    moduleCode: string;
    moduleId: ModuleId;
    questionCount: number;
    familyCount: number;
    tierRange: [number, number];
  }[];
  /** Family coverage: familyId → question count */
  familyCoverage: Record<string, number>;
  /** Errors encountered during load */
  errors: string[];
  /** Warnings encountered during load */
  warnings: string[];
  /** Visual registry sync status */
  visualRegistrySync: {
    inSync: boolean;
    missingFromGovernance: string[];
    missingFromRegistry: string[];
  };
  /** Timestamp */
  generatedAt: string;
}

/**
 * Generate a structured consolidation report from the loaded seed data.
 * Safe to call at any time — reads from the already-built SEED_COLLECTIONS.
 */
export function getConsolidationReport(): ConsolidationReport {
  const modules: ConsolidationReport['modules'] = [];
  const familyCoverage: Record<string, number> = {};
  let totalQuestions = 0;
  for (const [moduleId, collection] of SEED_COLLECTIONS) {
    const families = new Set<string>();
    let minTier = 16;
    let maxTier = 1;
    for (const q of collection.questions) {
      totalQuestions++;
      families.add(q.familyId);
      familyCoverage[q.familyId] = (familyCoverage[q.familyId] ?? 0) + 1;
      if (q.tier < minTier) minTier = q.tier;
      if (q.tier > maxTier) maxTier = q.tier;
    }
    modules.push({
      moduleCode: collection.moduleCode,
      moduleId,
      questionCount: collection.questions.length,
      familyCount: families.size,
      tierRange: [minTier, maxTier]
    });
  }

  // Visual registry sync check (§5.4)
  let visualSync: ConsolidationReport['visualRegistrySync'] = {
    inSync: true,
    missingFromGovernance: [],
    missingFromRegistry: []
  };
  // Visual registry sync check skipped here to avoid circular dependency.
  // Use governance/pipeline/visualRegistrySync.checkVisualRegistrySync() directly
  // from a higher-level consumer (e.g., diagnostics page) instead.

  return {
    totalSeedFiles: SEED_COLLECTIONS.size,
    totalQuestions,
    rejectedQuestions: _buildRejectedCount,
    modules,
    familyCoverage,
    errors: [..._buildErrors],
    warnings: [..._buildWarnings],
    visualRegistrySync: visualSync,
    generatedAt: new Date().toISOString()
  };
}

/** Get a specific module's seed collection */
export function getSeedCollection(moduleId: ModuleId): SeedCollection | undefined {
  return SEED_COLLECTIONS.get(moduleId);
}

/** Get all seed questions across all modules */
export function getAllSeedQuestions(): SeedQuestion[] {
  const all: SeedQuestion[] = [];
  for (const collection of SEED_COLLECTIONS.values()) {
    all.push(...collection.questions);
  }
  return all;
}

/** Get seed questions for a specific module */
export function getModuleSeedQuestions(moduleId: ModuleId): SeedQuestion[] {
  return SEED_COLLECTIONS.get(moduleId)?.questions ?? [];
}

/** Get seed questions filtered by tier */
export function getSeedQuestionsByTier(moduleId: ModuleId, tier: number): SeedQuestion[] {
  return getModuleSeedQuestions(moduleId).filter(q => q.tier === tier);
}

/** Get seed questions filtered by familyId */
export function getSeedQuestionsByFamily(familyId: string): SeedQuestion[] {
  const all: SeedQuestion[] = [];
  for (const collection of SEED_COLLECTIONS.values()) {
    for (const q of collection.questions) {
      if (q.familyId === familyId) all.push(q);
    }
  }
  return all;
}

/**
 * Convert a SeedQuestion to QuestionCore (the public question type).
 */
function seedToCore(seed: SeedQuestion): QuestionCore {
  return {
    id: seed.id,
    module: seed.module,
    question: seed.question,
    questionType: seed.questionType as QuestionType,
    citoDifficulty: seed.citoDifficulty,
    citoDiscrimination: seed.citoDiscrimination,
    skill: seed.skill,
    subSkill: seed.subSkill,
    familyId: seed.familyId,
    context: seed.context,
    options: seed.options,
    correctAnswer: seed.correctAnswer,
    isAnchor: seed.isAnchor,
    data: seed.data,
    // Canonical feedback + visual aids (v3.5 fix — Problem 34: was missing, causing data loss in diagnostic path)
    feedback: seed.feedback,
    stimulusAids: seed.stimulusAids,
    supportAids: seed.supportAids,
    // Taxonomy-authoritative fields (tier is canonical)
    tier: seed.tier,
    levelCode: seed.levelCode,
    group: seed.group,
    conceptId: seed.conceptId,
    variantIndex: seed.variantIndex,
    // FG2 Phase 2 — equivalence class for breadth/spread collapsing (§S10).
    equivalenceClass: seed.equivalenceClass,
    // Pedagogical contract (PEDAGOGICAL_CONTRACT_SPEC §3)
    pedagogy: seed.pedagogy
  };
}

/**
 * Run comprehensive governance validation on all seed questions using the pipeline.
 * Replaces the legacy validateSeedQuestionsReport from seedValidator.ts (Step 3.11).
 */
export async function validateSeedQuestionsGovernanceReport() {
  const {
    runPipelineBatch
  } = await import('../../governance/pipeline/orchestrator');
  const allQuestions = getAllSeedQuestions();
  // Convert SeedQuestion → Question shape for pipeline
  const asQuestions = allQuestions.map(q => ({
    ...q,
    questionType: q.questionType as QuestionType
  }));
  return runPipelineBatch(asQuestions);
}
export interface DiagnosticQuestionWithSource extends SeedQuestion {
  __sourceFile: string;
  __sourceSeedKey?: string;
}

/**
 * Dev-only helper: normalized questions enriched with source provenance.
 */
export function getAllQuestionsWithSources(): DiagnosticQuestionWithSource[] {
  return getAllSeedQuestions().map(question => ({
    ...question,
    __sourceFile: question.__sourceFile ?? 'unknown:source-file-not-available'
  }));
}